import { Stack } from '@mantine/core';
import {
	useApiAggregateEntityKeysForIntegration,
	useSetIntegrationExclusionFilters,
	useSetIntegrationWhitelistFilters,
} from '@repo/api-codegen';
import type { FilterOption } from '@repo/common/components/Filter/types';
import {
	FilterDropdownType,
	FilterOperator,
	FilterOptionType,
} from '@repo/common/components/Filter/types';
import { sentenceCase } from '@repo/common/utils/stringUtils';
import { Text, Title } from '@repo/foundations';
import { memo, useCallback } from 'react';
import {
	integrationsQueryKeyFactory,
	queryClient,
	type IIntegration,
} from '../../../../../api';
import { IntegrationResourceFilters } from './IntegrationResourceFilters';

const CUSTOM_INTEGRATION_WHITELIST_FILTER_OPTIONS: {
	[key: string]: FilterOption;
} = {
	[FilterOptionType.TITLE]: {
		label: 'Title',
		type: FilterOptionType.TITLE,
		field: 'title',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.String,
			defaultOperator: FilterOperator.Is,
			inputPlaceholder: 'Enter a title',
			hasIsNotSetOption: false,
		},
	},
	[FilterOptionType.NATIVE_TYPE]: {
		label: 'Type',
		type: FilterOptionType.NATIVE_TYPE,
		field: FilterOptionType.NATIVE_TYPE,
		filterDropdownConfig: {
			valueLabelFormatter: sentenceCase,
			dropdownType: FilterDropdownType.String,
			defaultOperator: FilterOperator.Is,
			inputPlaceholder: 'Enter an type',
			hasIsNotSetOption: false,
		},
	},
};

const CUSTOM_INTEGRATION_EXCLUSION_FILTER_OPTIONS: {
	[key: string]: FilterOption;
} = {
	title: {
		label: 'Title',
		type: FilterOptionType.TITLE,
		field: 'title',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.String,
			defaultOperator: FilterOperator.Contains,
			inputPlaceholder: 'Enter a title',
			hasIsNotSetOption: false,
		},
	},
	native_type: {
		label: 'Type',
		type: FilterOptionType.NATIVE_TYPE,
		field: FilterOptionType.NATIVE_TYPE,
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.String,
			defaultOperator: FilterOperator.Is,
			inputPlaceholder: 'Enter an type',
			hasIsNotSetOption: false,
			valueLabelFormatter: sentenceCase,
		},
	},
};

export const FiltersPreferences = memo(
	({ integration }: { integration: IIntegration }) => {
		const { data: filterOptions } = useApiAggregateEntityKeysForIntegration(
			{
				pathParams: {
					integrationId: integration.id,
				},
				queryParams: {
					entity_keys: [FilterOptionType.NATIVE_TYPE],
				},
			},
			{
				select(data) {
					return {
						exclusion_filters: Object.entries(
							CUSTOM_INTEGRATION_EXCLUSION_FILTER_OPTIONS
						).reduce(
							(acc, [key, value]) => ({
								...acc,
								[key]: {
									...value,
									filterDropdownConfig: {
										...value.filterDropdownConfig,
										prefilledOptions: (data[value.type] ?? []).map(
											(option) => ({
												label: sentenceCase(option),
												value: option,
											})
										),
									},
								},
							}),
							{} as Record<string, FilterOption>
						),
						whitelist_filters: Object.entries(
							CUSTOM_INTEGRATION_WHITELIST_FILTER_OPTIONS
						).reduce(
							(acc, [key, value]) => ({
								...acc,
								[key]: {
									...value,
									filterDropdownConfig: {
										...value.filterDropdownConfig,
										prefilledOptions: (data[value.type] ?? []).map(
											(option) => ({
												label: sentenceCase(option),
												value: option,
											})
										),
									},
								},
							}),
							{} as Record<string, FilterOption>
						),
					};
				},
			}
		);

		const { mutateAsync: persistExclusionFilters } =
			useSetIntegrationExclusionFilters({
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: integrationsQueryKeyFactory.byId(integration.id),
					});
				},
			});
		const { mutateAsync: persistWhitelistFilters } =
			useSetIntegrationWhitelistFilters({
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: integrationsQueryKeyFactory.byId(integration.id),
					});
				},
			});

		const updateExclusionFilters = useCallback(
			(key: string, newFilters: string[]) => {
				persistExclusionFilters({
					pathParams: {
						integrationId: integration.id,
					},
					body: {
						[key]: newFilters,
					},
				});
			},
			[persistExclusionFilters, integration.id]
		);

		const updateWhitelistFilters = useCallback(
			(key: string, newFilters: string[]) => {
				persistWhitelistFilters({
					pathParams: {
						integrationId: integration.id,
					},
					body: {
						[key]: newFilters,
					},
				});
			},
			[persistWhitelistFilters, integration.id]
		);

		return (
			<Stack spacing="md">
				<Stack spacing={0}>
					<Title size="md">Filtering</Title>
					<Text size="sm" color="text/secondary/default">
						Apply additional filters for what is synced based on resource names
					</Text>
				</Stack>
				<Stack spacing="sm">
					<IntegrationResourceFilters
						filters={integration.whitelist_filters}
						description={
							<>
								<Text size="sm">
									<strong>Whitelist </strong>
									resources that match any of the following filters.
								</Text>
								<Text size={'xs'} color={'text/secondary/default'}>
									Resources matched here will be included, even if they match an
									exclusion filter.
								</Text>
							</>
						}
						filterOptions={filterOptions?.whitelist_filters ?? {}}
						handleUpdate={updateWhitelistFilters}
					/>
					<IntegrationResourceFilters
						filters={integration.exclusion_filters}
						description={
							<Text size="sm">
								<strong>Exclude </strong>
								resources that match any of the following filters
							</Text>
						}
						filterOptions={filterOptions?.exclusion_filters ?? {}}
						handleUpdate={updateExclusionFilters}
					/>
				</Stack>
			</Stack>
		);
	}
);
